// One place where a request leaves this application, and one vocabulary for what
// came back.
//
// EVERY REFUSAL IS A NAMED OUTCOME AND NOTHING THROWS PAST THIS FILE. A thrown
// exception in a React render is a blank page, and a blank page on a console is
// indistinguishable from the service being down — which is the one diagnosis a
// client will act on by telephoning. xp/app/src/boot.ts makes the same argument
// about a tablet and a stand; the reader here is a client rather than a crew
// member, which makes it stronger rather than weaker.
//
// TWO LAYERS, AND THE SPLIT IS THE DESIGN.
//
// `ask` is the wire: it sends, it never throws, and it reports a status and a
// parsed body. It makes no judgement about what a status means.
//
// `failureFor` is the judgement, and it is only correct for a request that
// carried a session. That is why it is a separate function rather than something
// `ask` does on the way out: the login routes answer 401 to mean "that code is
// wrong", and the report routes answer 401 to mean "that session is dead". One
// mapper for both would have to be wrong for one of them, and the direction it
// would be wrong in is signing somebody out of a session they never had — or,
// worse, keeping one that is already revoked.
//
// THE STATUS CODES ARE NOT INTERCHANGEABLE.
//
//   401 on a report means the credential is not a live session. reports/auth.ts
//   answers one 401 for four causes — no such session, revoked, expired,
//   identity disabled — deliberately, so that nobody holding a guessed token
//   learns it was nearly right. The console must therefore not try to
//   distinguish them, must not retry, and must forget the token.
//
//   403 means the credential is live and is not enough. A nova_admin who signed
//   in without a second factor is the case that exists today; a client viewer
//   with no grant for the activation is the other. Both are signed in. The
//   caller must not clear the session — session/store.ts has the argument about
//   the loop that produces.
//
//   503 audit_not_recorded is its own outcome because it is the one refusal that
//   means "ask again shortly" and nothing was read. Collapsing it into a general
//   failure would tell somebody their report is broken when the service is
//   momentarily unable to record the read.
//
// THE TRANSPORT IS INJECTED, for the reason xp/station gives for taking its store
// by injection: this file has behaviour worth asserting — which status becomes
// which outcome, and what is and is not sent — and asserting it through a real
// network is asserting the network. check.mjs hands in a transport that records
// every request and answers from a script.

import {
  ACTIVATIONS_ENDPOINT,
  AUTH_ENDPOINTS,
  exportHistoryUrl,
  exportUrl,
  REPORT_METHODS,
  reportUrl,
  SESSION_TOKEN_HEADER,
  type ReportName,
} from "./endpoints.js";
import type { SessionToken } from "../session/store.js";
import { beginRead, endRead } from "./freshness.js";

/** Exactly the part of `fetch` this console uses. */
export type Transport = (url: string, init: RequestInit) => Promise<Response>;

export type ApiOptions = {
  readonly baseUrl: string;
  readonly transport: Transport;
};

/** What a refusal body may carry. Every field optional, because it is somebody else's JSON. */
export type ErrorBody = {
  readonly error?: unknown;
  readonly detail?: unknown;
  readonly needs_second_factor?: unknown;
};

/**
 * What came back, without an opinion about it.
 *
 * `status: null` is the case where nothing came back at all. It is a status
 * rather than a separate variant so that every caller has to handle it: a
 * `switch` on a union would let somebody forget the network, and the network is
 * the thing that actually happens.
 */
export type Answer =
  | { readonly ok: true; readonly status: number; readonly body: unknown; readonly replacement: string | null }
  | {
      readonly ok: false;
      readonly status: number | null;
      readonly body: ErrorBody | null;
      /**
       * The token to carry from here on, where the service rotated one.
       *
       * ON THE REFUSALS AS WELL AS THE SUCCESSES, AND THAT IS THE WHOLE REASON IT
       * IS ON `Answer` RATHER THAN ON THE SUCCESS BRANCH. A nova_admin without a
       * second factor gets a 403 from a request that rotated; so does anybody
       * who hits the rate limiter. Reading the replacement only off a 200 would
       * leave those callers holding a spent token, and the next thing they did
       * would be answered as reuse and end their session — a person refused one
       * screen, and signed out for asking.
       *
       * Null on a 401, and not by a special case: the service does not rotate a
       * token it did not accept, so there is no header to read. That falls out
       * of the server rather than being arranged here.
       */
      readonly replacement: string | null;
    };

export type Failure =
  /** The session is not live. The caller clears it and sends the person to the login screen. */
  | { readonly kind: "session_ended"; readonly detail: string }
  /** Live credential, not permitted. The session stays. */
  | { readonly kind: "refused"; readonly detail: string }
  /** The service could not record the read, so it did not do it. Retryable. */
  | { readonly kind: "unavailable"; readonly detail: string }
  /**
   * We asked wrongly, which is our bug rather than the person's. Shown with the
   * server's own sentence, because that sentence names the argument at fault and
   * a paraphrase would not.
   */
  | { readonly kind: "malformed"; readonly detail: string }
  /**
   * The same 400, when the argument the server rejected came out of the address
   * bar rather than out of this console's own state.
   *
   * The distinction is who gets blamed. `malformed` says "we asked wrongly",
   * which is the right sentence when the console built the request and got it
   * wrong, and the wrong one when the console faithfully passed along a
   * truncated paste. Somebody who half-copied a link to one person's record was
   * told the console had a bug, and the true fix for that is theirs and takes a
   * second.
   *
   * Only a caller can tell these apart, because only a caller knows where its
   * arguments came from. `asMisaddressed` below is how one says so.
   */
  | { readonly kind: "misaddressed"; readonly detail: string }
  /**
   * Nothing came back, or what came back was not the shape of anything.
   *
   * Distinct from `unavailable` because the two send somebody to different
   * places: this one is a network, a proxy, or a console pointed at something
   * that is not the API. It is the outcome an empty base URL used to produce on a
   * tablet as a confusing report that the venue's network was down.
   */
  | { readonly kind: "unreachable"; readonly detail: string };

export type Read<T> = { readonly ok: true; readonly value: T } | ({ readonly ok: false } & Failure);

/**
 * Send one request. Never throws, never retries, never carries a credential it
 * was not given.
 *
 * There is deliberately no retry anywhere in this package. A read that failed
 * because the audit could not be recorded is one the person should ask for again
 * when they want it, and a console that quietly retried would turn one recorded
 * read into several in report_access — which is the table a client will one day
 * read to find out who at Nova looked at their attendee list.
 */
export async function ask(
  options: ApiOptions,
  path: string,
  init: RequestInit & { method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" },
): Promise<Answer> {
  if (options.baseUrl === "") {
    // The §104 failure, refused rather than performed. An empty base URL makes
    // every request relative, so it would be aimed at the console's own origin
    // where nothing is listening, and the browser would report a failure that
    // reads as the network being down. It happened on a tablet on 2026-08-26 and
    // cost twenty minutes; here it would be a client being told the service is
    // unavailable.
    //
    // Unreachable in practice — the build refuses to produce an artefact with no
    // server and App refuses to render one — and kept for the reason boot.ts
    // keeps its own copy of this check: something that produces a bundle without
    // going through vite.config.ts reintroduces the route silently, and this is
    // what would catch it.
    return { ok: false, status: null, body: null, replacement: null };
  }

  let response: Response;
  try {
    response = await options.transport(`${options.baseUrl}${path}`, init);
  } catch {
    // The error is not inspected and not logged. A fetch failure message is
    // browser-specific, none of it means anything to the person reading this
    // screen, and an error object off a request that carried an Authorization
    // header is a plausible carrier for that header — which is the reasoning
    // api/src/auth/login.ts uses about error objects from the mail delivery.
    return { ok: false, status: null, body: null, replacement: null };
  }

  const body = await readBody(response);
  const replacement = replacementIn(response);

  return response.ok
    ? { ok: true, status: response.status, body, replacement }
    : { ok: false, status: response.status, body: asErrorBody(body), replacement };
}

/**
 * The replacement session token off a response, or null.
 *
 * READ HERE, WHERE THE `Response` IS, AND ADOPTED IN `authenticated`, WHERE THE
 * TOKEN IS. Splitting it that way is what keeps this file's own rule — `ask` is
 * the wire and makes no judgement — while still having exactly one place that
 * knows the header's name.
 *
 * A HEADER A BROWSER CANNOT READ COMES BACK NULL AND LOOKS EXACTLY LIKE NO
 * ROTATION, which is the failure worth naming because it is entirely silent and
 * it is a deployment mistake rather than a code one. If api/src/cors.ts stopped
 * listing this header in `access-control-expose-headers`, `headers.get` would
 * answer null on every response, the console would never adopt anything, and
 * every session would be revoked for reuse one grace window after its first
 * request — with nothing in the browser saying why. console/check.mjs asserts
 * against the API's own constant that the two agree, rather than against a
 * string written here.
 *
 * The empty string is treated as absent. A proxy that strips a value and leaves
 * the name behind would otherwise have this console adopt "" and send
 * `Authorization: Bearer ` on the next request.
 */
function replacementIn(response: Response): string | null {
  const value = response.headers.get(SESSION_TOKEN_HEADER);
  return typeof value === "string" && value !== "" ? value : null;
}

/** A request with no credential. Only the two login routes reach this. */
export async function unauthenticated(
  options: ApiOptions,
  path: string,
  body: unknown,
): Promise<Answer> {
  return ask(options, path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * A request carrying the session, and the one place a replacement is adopted.
 *
 * The token is a parameter rather than something read from the store in here, so
 * that this file holds no reference to the storage and there is exactly one
 * moment — the call site — at which the token is in a variable. It goes into a
 * header and into nothing else: not a query string, not a body, not a log.
 *
 * A `SessionToken` RATHER THAN A STRING SINCE ROTATION LANDED, and the change is
 * doing two jobs. The obvious one is that a replacement has somewhere to go. The
 * one that decided the shape is that nine surfaces hold this value as a React
 * prop with a fetching effect keyed on it, and a string that changes on every
 * request is nine infinite loops — session/store.ts argues it at length.
 *
 * EVERY AUTHENTICATED REQUEST IN THIS PACKAGE GOES THROUGH HERE, which is what
 * makes one adoption site sufficient. That was already true and already the
 * reason this function exists; rotation is the first thing that depends on it
 * being true rather than merely tidy. A future call that built its own `ask`
 * with an Authorization header would rotate and drop the replacement, and would
 * have its session revoked for reuse a minute later — so console/check.mjs
 * asserts that no module outside this file writes an `authorization` header.
 */
export async function authenticated(
  options: ApiOptions,
  token: SessionToken,
  request: {
    path: string;
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    /**
     * Sent as the JSON body, when there is one.
     *
     * Absent and present are different requests rather than the same request
     * with an empty body: `undefined` sends no body and no content-type, which is
     * what the two enrolment POSTs that carry nothing need. Sending `"{}"` with a
     * content-type instead would be a body the schema on the far side has to
     * accept, and the enrolment confirmation's schema rejects a body without a
     * `code` — so the difference is a 400 rather than a matter of taste.
     */
    json?: unknown;
  },
): Promise<Answer> {
  beginRead();
  try {
    const answer = await ask(options, request.path, {
      method: request.method,
      // Read at the moment of sending rather than captured earlier. A token read
      // when a component rendered is a token that may have been replaced by
      // another request in flight, and sending it would be presenting a spent
      // generation — inside the grace window, so it works, and it is still the
      // wrong thing to be relying on.
      headers: {
        authorization: `Bearer ${token.current()}`,
        ...(request.json === undefined ? {} : { "content-type": "application/json" }),
      },
      // The token stays in the header and is never merged into this. A body is a
      // thing proxies log and browsers keep in memory dumps for longer than a
      // header, and there is no reason for a credential to be in both places.
      ...(request.json === undefined ? {} : { body: JSON.stringify(request.json) }),
    });

    // BEFORE THE ANSWER IS RETURNED, AND WITHOUT LOOKING AT THE STATUS. The
    // service rotates a token it accepted, whatever it then decided to answer, so
    // a 403 or a 429 carries a replacement exactly as a 200 does. A caller that
    // adopted only on success would strand somebody who was refused one screen.
    //
    // Adopting a replacement is not a decision about whether the request
    // succeeded, which is why it happens here and not in `failureFor`.
    if (answer.replacement !== null) token.adopt(answer.replacement);
    return answer;
  } finally {
    endRead();
  }
}

/** One activation's report, as a value or a named failure. */
export async function readReport<T>(
  options: ApiOptions,
  token: SessionToken,
  report: ReportName,
  activationId: string,
  /**
   * A query string, "?" and all, or nothing. Only the operator view takes one,
   * the days of the run to read (TASKS §143, step 7); opt-in and the survey
   * refuse a window on purpose and are never sent one.
   */
  query = "",
): Promise<Read<T>> {
  const answer = await authenticated(options, token, {
    path: reportUrl(report, activationId) + query,
    // The service's own verb for this report rather than GET for all four.
    // Reconciliation is a POST because it takes a device roster; asking for it
    // with a GET is Fastify's 404, which this file would map to "the service
    // answered 404, which this console has no sentence for" — a plausible
    // sentence about a report that is answering perfectly well.
    //
    // No body goes with the POST, which is the request for the default roster.
    // reconciliation.ts reads an absent `expected_devices` as "use the devices
    // enrolled for this activation" and an empty array as "the crew says no
    // device was on the stand", and those are different reports. This console
    // has no way to ask a crew member anything, so it sends neither and takes
    // the enrolment roster — see Reconciliation.tsx.
    method: REPORT_METHODS[report],
  });

  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  return { ok: true, value: answer.body as T };
}

/**
 * Which activations this session may read.
 *
 * The request a caller makes when they have not got an activation id, which
 * until 2026-08-28 was a request there was no endpoint for — so the console's
 * landing screen was a box you pasted a uuid into.
 *
 * IT TAKES NO ARGUMENTS AND THAT IS THE ENDPOINT'S PROPERTY RATHER THAN THIS
 * FUNCTION'S TASTE. operator/src/activations.ts runs one statement with no
 * parameters and no `where`, and what comes back is decided by the policies in
 * 004 against the credential in the header. There is nothing for a browser to
 * pass, which also means there is nothing a browser could pass wrongly.
 */
export async function readActivations<T>(
  options: ApiOptions,
  token: SessionToken,
  /**
   * The column and direction the reader pressed on the directory, or null for
   * the service's own order. The only argument the directory takes: an order
   * changes the sequence of what the policies return and never which rows.
   */
  order: { readonly key: string; readonly dir: "asc" | "desc" } | null = null,
  /**
   * One state to narrow the list to on the server (151): open, paused, closed
   * or sealed, or null for every one. Like the order, it never widens what the
   * policies return.
   */
  state: string | null = null,
): Promise<Read<T>> {
  const parts = [
    ...(order === null ? [] : [`sort=${encodeURIComponent(order.key)}`, `dir=${order.dir === "desc" ? "desc" : "asc"}`]),
    ...(state === null ? [] : [`state=${encodeURIComponent(state)}`]),
  ];
  const query = parts.length === 0 ? "" : `?${parts.join("&")}`;
  const answer = await authenticated(options, token, { path: `${ACTIVATIONS_ENDPOINT}${query}`, method: "GET" });

  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  return { ok: true, value: answer.body as T };
}

/**
 * One activation's attendees, for a purpose that has to be named.
 *
 * Its own function rather than a `readReport("export", …)` with an argument,
 * because it is the one read on this console that hands over personal data and
 * it should not be reachable by the same call the other three make. A caller
 * cannot arrive here by iterating a list of reports; they have to mean it.
 */
export async function readExport<T>(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  purpose: string,
): Promise<Read<T>> {
  const answer = await authenticated(options, token, {
    path: exportUrl(activationId, purpose),
    method: REPORT_METHODS.export,
  });

  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  return { ok: true, value: answer.body as T };
}

/**
 * What files of this activation's people have already been produced.
 *
 * THE ONE READ IN THIS FILE THAT A CLIENT'S OWN VIEWER MAKES ABOUT THE EXPORT,
 * and it sits directly under `readExport`, which that same viewer is refused.
 * The pair is deliberate and the distinction is the endpoint's rather than this
 * console's: producing a file of somebody's attendees is held back on an open
 * commercial question about entitlement, and being told what has already been
 * produced is not that question. Nothing this returns names an attendee, and
 * nothing it returns names the Nova person who ran a file either.
 *
 * SO THERE IS NO ROLE TEST HERE OR AT THE CALL SITE. If this ever needs one,
 * the endpoint has stopped answering a client and the fix is there.
 *
 * Its own function rather than a sixth `readReport`, for the reason
 * `exportHistoryUrl` gives: `ReportName` is the five audited reports and this
 * is not one of them.
 */
export async function readExportHistory<T>(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<Read<T>> {
  const answer = await authenticated(options, token, {
    path: exportHistoryUrl(activationId),
    method: "GET",
  });

  if (!answer.ok) return { ok: false, ...failureFor(answer) };
  return { ok: true, value: answer.body as T };
}

/**
 * End the session at the server as well as here.
 *
 * The endpoint exists for exactly this and api/src/auth/routes.ts says so: a
 * revocation column nothing writes to is a claim resting on somebody running SQL
 * by hand. Forgetting the token locally without calling this leaves a live
 * credential in auth_session until its expiry, which on a shared machine is the
 * difference between signing out and closing a tab.
 *
 * The answer is returned rather than swallowed, and the caller signs out
 * regardless of it. The server sends the same body whether or not the token was
 * live — telling a caller otherwise tells somebody who found a token in a log
 * file whether it is worth trying — so there is nothing to branch on, and a
 * failed revoke must not trap a person in a session they have asked to leave.
 * What it must not do is be invisible, which is why it comes back rather than
 * being discarded in here.
 */
export async function endSession(options: ApiOptions, token: SessionToken): Promise<Answer> {
  return authenticated(options, token, { path: AUTH_ENDPOINTS.endSession, method: "DELETE" });
}

/**
 * What a status means for a request that carried a session.
 *
 * Correct only for that case, per the header. The login routes have their own
 * reading in signIn.ts and must not use this one.
 */
/**
 * For a read whose key came out of the address bar.
 *
 * Applied by the caller and never inside `failureFor`, because `failureFor`
 * sees an HTTP answer and an HTTP answer does not carry where its arguments
 * were typed. A surface that reads an identifier out of the URL knows, and is
 * the only thing that does.
 *
 * Everything other than the 400 passes through: a session that ended, a refusal
 * and an unreachable service mean the same thing however the request was keyed.
 */
export function asMisaddressed(failure: Failure): Failure {
  return failure.kind === "malformed" ? { kind: "misaddressed", detail: failure.detail } : failure;
}

export function failureFor(answer: Extract<Answer, { ok: false }>): Failure {
  const said = typeof answer.body?.detail === "string" && answer.body.detail !== "" ? answer.body.detail : null;

  if (answer.status === null) {
    return {
      kind: "unreachable",
      detail:
        "The console could not reach the service. That is a network or a server that is not answering, and nothing has been read.",
    };
  }

  if (answer.status === 401) {
    return {
      kind: "session_ended",
      // The server's sentence where there is one. reports/auth.ts's 401 says
      // "that credential is not a live session and is not the admin token",
      // which is deliberately vague about which of four causes it was and is
      // more honest than anything this file could invent.
      detail: said ?? "That sign-in is no longer live. Sign in again.",
    };
  }

  if (answer.status === 403) {
    return {
      kind: "refused",
      // Passed through verbatim, and this one matters more than the others. The
      // 403 a nova_admin without a second factor gets carries the instruction
      // that resolves it — enrol an authenticator and sign in again — and a
      // paraphrase would drop it. The console does not know that rule and should
      // not learn it: the server owns who may read what, and a console with its
      // own copy of the rule is a console that disagrees with it after the next
      // server change.
      detail: said ?? "You are signed in, and this is not something this sign-in may read.",
    };
  }

  if (answer.status === 503) {
    return {
      kind: "unavailable",
      detail:
        said ??
        "The service could not record this read, so it did not perform it. Nothing is wrong with the data. Try again shortly.",
    };
  }

  if (answer.status === 400) {
    return {
      kind: "malformed",
      detail: said ?? "The console asked for that in a way the server did not accept.",
    };
  }

  return {
    kind: "unavailable",
    detail: said ?? `The service answered ${answer.status}, which this console has no sentence for.`,
  };
}

/**
 * The response body as JSON, or null.
 *
 * Null rather than a throw for a body that is not JSON, because the case that
 * produces one is a proxy, a load balancer or a captive portal answering instead
 * of the API — and an HTML error page parsed as JSON would otherwise take the
 * whole screen out with a syntax error.
 */
async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

const asErrorBody = (body: unknown): ErrorBody | null =>
  typeof body === "object" && body !== null ? (body as ErrorBody) : null;
